/**
 * WarmUpCalculator — dialog that suggests warm-up pyramids for a working set.
 *
 * Props:
 *   open, onClose                 — dialog open state (caller-controlled)
 *   initialWeight, initialReps    — prefill (e.g. from the first working set)
 *   exercise                      — { name, equipment, is_barbell } (controls defaults)
 *   units                         — 'kg' | 'lbs'
 *   onApply(sets)                 — optional: called with the chosen sets if the
 *                                   caller wants to pre-fill them into a workout
 *
 * Usage patterns:
 *   Standalone (CoachCalculators page): render without onApply — calculator-only
 *   Inside workout: pass exercise + initialWeight + onApply to pre-fill sets.
 */

import React, { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Flame, Clock, RotateCcw } from 'lucide-react';
import {
  calculateWarmupSets,
  defaultAlgorithmFor,
  WARMUP_ALGORITHMS,
  WARMUP_ALGORITHM_LABELS,
} from '../utils/warmupUtils';

export default function WarmUpCalculator({
  open,
  onClose,
  initialWeight = 0,
  initialReps = 5,
  exercise = {},
  units = 'kg',
  onApply,
}) {
  const { t } = useTranslation();
  const [weight, setWeight] = useState(initialWeight || 0);
  const [reps, setReps] = useState(initialReps || 5);
  const [algorithm, setAlgorithm] = useState(() => defaultAlgorithmFor(exercise));

  // Reset state when the dialog reopens with new context
  useEffect(() => {
    if (open) {
      setWeight(initialWeight || 0);
      setReps(initialReps || 5);
      setAlgorithm(defaultAlgorithmFor(exercise));
    }
  }, [open, initialWeight, initialReps, exercise]);

  const warmupSets = useMemo(
    () => calculateWarmupSets({ workingWeight: Number(weight), workingReps: Number(reps), exercise, algorithm, units }),
    [weight, reps, exercise, algorithm, units],
  );

  const exerciseLabel = exercise?.name || t('warmup.genericExercise', 'Working set');

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose?.()}>
      <DialogContent className="max-w-md" data-testid="warmup-calculator">
        <DialogHeader>
          <DialogTitle
            className="flex items-center gap-2"
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
          >
            <Flame className="h-5 w-5 text-primary" />
            <span>
              {t('warmup.title', 'Warm-Up Calculator').toUpperCase()}{' '}
              <span className="text-primary">·</span>{' '}
              <span className="text-muted-foreground text-sm font-normal">
                {exerciseLabel}
              </span>
            </span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Inputs */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="warmup-weight" className="text-xs uppercase tracking-wide text-muted-foreground">
                {t('warmup.workingWeight', 'Working weight')} ({units})
              </Label>
              <Input
                id="warmup-weight"
                type="number"
                step="0.5"
                min="0"
                value={weight}
                onChange={(e) => setWeight(e.target.value)}
                data-testid="warmup-weight-input"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="warmup-reps" className="text-xs uppercase tracking-wide text-muted-foreground">
                {t('warmup.workingReps', 'Working reps')}
              </Label>
              <Input
                id="warmup-reps"
                type="number"
                min="1"
                value={reps}
                onChange={(e) => setReps(e.target.value)}
                data-testid="warmup-reps-input"
              />
            </div>
          </div>

          {/* Algorithm toggle */}
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">
              {t('warmup.method', 'Method')}
            </Label>
            <div className="grid grid-cols-3 gap-1.5">
              {Object.values(WARMUP_ALGORITHMS).map((alg) => (
                <button
                  key={alg}
                  type="button"
                  onClick={() => setAlgorithm(alg)}
                  className={`px-2 py-2 rounded-lg text-xs font-medium transition-colors ${
                    algorithm === alg
                      ? 'bg-primary text-white'
                      : 'bg-muted/40 text-muted-foreground hover:bg-muted/60'
                  }`}
                  data-testid={`warmup-algo-${alg}`}
                >
                  {WARMUP_ALGORITHM_LABELS[alg]}
                </button>
              ))}
            </div>
          </div>

          {/* Result */}
          <div className="rounded-xl border border-border/60 bg-muted/30 overflow-hidden">
            <div className="px-3 py-2 text-xs font-bold uppercase tracking-wider text-muted-foreground border-b border-border/40 flex items-center justify-between">
              <span>{t('warmup.suggestedSets', 'Suggested warm-up')}</span>
              <span>
                {warmupSets.length} {warmupSets.length === 1 ? t('warmup.set', 'set') : t('warmup.sets', 'sets')}
              </span>
            </div>
            {warmupSets.length === 0 ? (
              <div className="px-3 py-6 text-center text-sm text-muted-foreground">
                {t('warmup.enterWeight', 'Enter a working weight to see your warm-up plan.')}
              </div>
            ) : (
              <div className="divide-y divide-border/40">
                {warmupSets.map((s, idx) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between px-3 py-2.5 text-sm"
                    data-testid={`warmup-set-${idx}`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="text-xs font-mono text-muted-foreground w-6">#{idx + 1}</span>
                      <span className="font-bold font-mono">
                        {s.weight}
                        <span className="text-xs text-muted-foreground ml-0.5">{units}</span>
                      </span>
                      <span className="text-muted-foreground">×</span>
                      <span className="font-bold font-mono">{s.reps}</span>
                      {s.label && (
                        <span className="text-[10px] font-semibold uppercase tracking-wide text-primary/80 bg-primary/10 px-1.5 py-0.5 rounded">
                          {s.label}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Clock className="h-3 w-3" />
                      {s.rest_seconds}s
                    </div>
                  </div>
                ))}
                <div className="flex items-center gap-3 px-3 py-2.5 text-sm bg-primary/5">
                  <span className="text-xs font-mono text-primary w-6">→</span>
                  <span className="font-bold font-mono text-primary">
                    {weight}
                    <span className="text-xs text-primary/70 ml-0.5">{units}</span>
                  </span>
                  <span className="text-primary/70">×</span>
                  <span className="font-bold font-mono text-primary">{reps}</span>
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-primary ml-auto">
                    {t('warmup.workingSet', 'Working set')}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            type="button"
            variant="ghost"
            onClick={() => setAlgorithm(defaultAlgorithmFor(exercise))}
            className="gap-1.5"
            data-testid="warmup-reset"
          >
            <RotateCcw className="h-3.5 w-3.5" />
            {t('common.reset', 'Reset')}
          </Button>
          {onApply && warmupSets.length > 0 && (
            <Button
              type="button"
              onClick={() => {
                onApply(warmupSets);
                onClose?.();
              }}
              data-testid="warmup-apply"
            >
              {t('warmup.useThese', 'Use these')}
            </Button>
          )}
          <Button type="button" variant="outline" onClick={onClose} data-testid="warmup-close">
            {t('common.close', 'Close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
